import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Injector,
  Input,
  Output,
} from '@angular/core';
import { AnalyticsService, ContextService, PlatformService, ProfileService } from '@core/services';
import { EventActionEnum, EventCategoryEnum } from '@core/services/analytics';
import { BillingV2Service } from '@core/services/lingo2-account/billing-v2.service';
import {
  BillingApiAccountAttachPaymentCardDto,
  BillingApiMerchantResponseDto,
  BillingApiPaymentResponseDto,
} from '@lingo2-billing-sdk/models';
import { BillingCorePaymentStatusEnum } from '@lingo2-billing-sdk/models/src/billing-core/billing-core-payment/types/enums/billing-core-payment-status.enum';
// TODO разобраться с Sentry : import * as Sentry from '@sentry/angular';
import { Store } from '@ngrx/store';
import { PaymentFail } from '@shared/checkout-wizards/components/payment-widget';
import { PaymentWidgetMerchantProcessingService } from '@shared/checkout-wizards/components/payment-widget/payment-widget-merchant-processing/payment-widget-merchant-processing.service';
import { AbstractCheckoutWizard } from '@shared/checkout-wizards/models/abstract-checkout-wizard';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import { StepByStepWizardStep } from '@shared/step-by-step-wizard';
import { getProfileSettings } from '@store/reducers/data.reducer';
import { IUserAccess, User } from 'lingo2-models';
import { isEqual } from 'lodash';
import { DeviceDetectorService } from 'ngx-device-detector';
import { filter, map, mergeMap, takeUntil } from 'rxjs';

type WizardContext = AddCardWizardDialogComponent;
type WizardViewType = 'payment' | 'error';

/** Мастер для добавления карты */
@Component({
  selector: 'app-add-card-wizard-dialog',
  templateUrl: './add-card-wizard-dialog.component.html',
  styleUrls: ['./add-card-wizard-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddCardWizardDialogComponent extends AbstractCheckoutWizard<WizardContext, WizardViewType> {
  @Input() public providerId: string;

  @Output()
  public closed = new EventEmitter<boolean>();

  public _me: User;
  private _settings: Partial<IUserAccess>;
  private sendData: BillingApiAccountAttachPaymentCardDto;
  public errorDescription = '';
  public screen: 'frame' | 'success' | 'fail' = 'frame';
  public merchant: BillingApiMerchantResponseDto;

  public payment: BillingApiPaymentResponseDto;

  public constructor(
    public errorNotificationService: ErrorNotificationService,
    protected profileService: ProfileService,
    protected contextService: ContextService,
    protected store: Store,
    public deviceService: DeviceDetectorService,
    protected readonly injector: Injector,
    protected readonly cdr: ChangeDetectorRef,
    protected readonly platform: PlatformService,
    protected billingV2Service: BillingV2Service,
    protected readonly merchantService: PaymentWidgetMerchantProcessingService,
    private analytics: AnalyticsService,
  ) {
    super(injector, cdr, platform);
  }

  public ngOnInit() {
    super.ngOnInit();

    this.contextService.me$
      .pipe(
        filter((me) => !!me),
        map((me) => {
          this._me = me;
          this.getMerchants();
          return me;
        }),
        mergeMap((me) => this.store.select(getProfileSettings)),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (settings) => {
          this._settings = settings;
          this.setupPaymentWidget();
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public onClose() {
    this.closed.emit(true);
  }

  /** Инициализация шагов мастера */
  protected initWizardSteps() {
    this.steps.addStep(
      new StepByStepWizardStep<WizardContext>({
        id: 'payment',
        title: 'payment-checkout-wizard.steps.user-service.payment',
        context: this,
        checks: [],
        visible: true,
        active: false,
      }),
    );
    this.cdr.detectChanges();
  }

  /** Настройка шагов мастера под набор входных параметров */
  protected setupWizardSteps() {
    this.steps.goStep('payment');
    this.activateView('payment');
    this.screen = 'frame';
    /** @see onLoadMeetingDone */
  }

  /** Выполнить обработку по активации шага мастера */
  protected onStepActivated(step: string) {
    /* не требуется */
  }

  protected onLoadMeetingDone() {
    /* не требуется */
  }

  public getMerchants() {
    this.billingV2Service
      .getMerchants()
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (merchants) => {
          this.merchant = merchants.find((merchant) => merchant.id === this.providerId);
          this.analytics.event(EventActionEnum.paywall_card_linking, EventCategoryEnum.ecommerce, [
            'add-card-wizard-dialog',
            this.merchant?.name,
          ]);
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  /** Настройка данных для проведения платежа */
  public setupPaymentWidget() {
    if (this.sendData) {
      const tempSendData = {
        email: this._settings?.email,
        phone: null,
        contractorId: null,
        providerId: this.providerId,
        countryCode: this._me?.country.alpha2Code,
        firstName: this._me?.first_name,
        lastName: this._me?.last_name,
      };
      if (isEqual(this.sendData, tempSendData)) {
        return;
      }
    }
    this.sendData = {
      email: this._settings?.email,
      phone: null,
      contractorId: null,
      providerId: this.providerId,
      countryCode: this._me?.country.alpha2Code,
      firstName: this._me?.first_name,
      lastName: this._me?.last_name,
    };
    this.billingV2Service
      .addCard(this.sendData)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (payment) => {
          switch (payment.status) {
            case BillingCorePaymentStatusEnum.INTENT: // Ручная оплата у мерчанта
              this.payment = payment;
              this.setTimeout(() => this.merchantService.pay(), 500);
              break;

            case BillingCorePaymentStatusEnum.SUCCEEDED: // Сразу оплачен
              this.onSuccess();
              break;

            case BillingCorePaymentStatusEnum.FAILED:
              this.errorDescription = JSON.stringify(payment);
              this.screen = 'fail';
              this.detectChanges();
              break;
          }
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'SAVE-PROBLEM');
          this.errorDescription = error.error ? error.error.message : JSON.stringify(error);
          this.screen = 'fail';
          this.detectChanges();
        },
      });
  }

  public onSuccess() {
    this.screen = 'success';
    this.detectChanges();
    this.setTimeout(() => {
      this.analytics.event(EventActionEnum.paywall_card_linked, EventCategoryEnum.ecommerce, [
        'add-card-wizard-dialog',
        this.merchant?.name,
      ]);
      this.onClose();
    }, 5000);
  }

  public onFail(err?: PaymentFail) {
    this.screen = 'fail';
    this.errorDescription = err.message;
    this.detectChanges();
  }
}
